import React from 'react'
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@the-platform-group/component-library/Table'
import { Typography } from '@the-platform-group/component-library'
import { getJobTitleAndOrgName } from 'pages/MergeProfiles/utils'

/**
 * JobDetailsTable - Read-only table displaying job field values with override highlighting
 * A reusable table component that displays job field values in a 3-column format:
 * 1. Field Name
 * 2. Merged Value - shows overridden value in green with source job name if override exists. Shows FactSet value in normal text if no override
 * 3. Overridden Value - shows original FactSet value in red with strikethrough if override exists. Empty if no override
 * @param {Object} props
 * @param {Array<Object>} props.fields - Array of field definitions [{ key, label }]
 * @param {Object} props.selectedTargetJob - The FactSet job to display
 * @param {Function} props.getFieldValue - Function to extract field value from job (job, fieldKey) => value
 * @param {Function} props.getOverrideValue - Function to get override info (fieldKey) => { value, sourceJob } | null
 *
 * @returns {React.Component} Table with 3 columns showing field comparisons
 */
const JobDetailsTable = ({ fields, selectedTargetJob, getFieldValue, getOverrideValue }) => {
  return (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            backgroundColor: 'background.secondary.default',
          }}
        >
          <TableHeaderCell
            sx={{
              borderBottom: '1px solid',
              borderColor: 'border.interactive',
            }}
          >
            {/* Empty header for field names column */}
          </TableHeaderCell>
          <TableHeaderCell
            sx={{
              fontWeight: 'bold',
              borderBottom: '1px solid',
              borderColor: 'border.interactive',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Merged Value
            </Typography>
          </TableHeaderCell>
          <TableHeaderCell
            sx={{
              fontWeight: 'bold',
              borderBottom: '1px solid',
              borderColor: 'border.interactive',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Overridden Value
            </Typography>
          </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map(field => {
          const factSetValue = getFieldValue(selectedTargetJob, field.key)
          const override = getOverrideValue(field.key)
          const hasOverride = !!override

          return (
            <TableRow
              key={field.key}
              sx={{
                '&:last-child td': { borderBottom: 'none' },
              }}
            >
              {/* Field Name Column */}
              <TableCell
                sx={{
                  backgroundColor: 'background.secondary.default',
                  borderBottom: '1px solid',
                  borderColor: 'border.secondary',
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {field.label}
                </Typography>
              </TableCell>

              {/* Merged Value Column */}
              <TableCell
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'border.secondary',
                }}
              >
                {hasOverride ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ color: 'success.main', fontWeight: 'medium' }}
                    >
                      {override.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Platform: {getJobTitleAndOrgName(override.sourceJob)})
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">{factSetValue}</Typography>
                )}
              </TableCell>

              {/* Overridden Value Column */}
              <TableCell
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'border.secondary',
                }}
              >
                {hasOverride && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'error.main',
                        textDecoration: 'line-through',
                      }}
                    >
                      {factSetValue}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (FactSet)
                    </Typography>
                  </>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default JobDetailsTable
